import { useClerk } from "@clerk/react";
import { type FormEvent, useId, useRef, useState } from "react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

// W-0add48e2 (#1711): the Desk signs in to Cleo Connect with an emailed 6-digit code, in this small form, not Clerk's
// prebuilt <SignIn>. On the 0.0.200 Desk the prebuilt form opened on a red "passkey_retrieval_failed" error: the
// instance allows passkey autofill, so <SignIn> starts a passkey request at mount, and @clerk/electron's passkeys
// provider (ClerkProvider passkeys=, mainManagedAuthShellElectron.tsx) drops the conditional-UI flag and sends it
// to macOS's native passkey API, which refuses a process without an application identifier. Passkey autofill,
// "Use passkey instead", "Use phone" and email link versus code are instance settings with no <SignIn> prop to
// turn them off, so the Desk asks only for the email, sends a code, and never touches a passkey.
// When Clerk asks for a step this form doesn't do (two-step verification, a protect check, device trust, a new
// password), it says so and offers Clerk's full sign-in, with the email prefilled, so such an account still signs in
// on the Desk. That form shows the passkey error, but it can finish those steps.
export const DESK_SIGN_IN_STRATEGY = "email_code";

type SignInAttempt = Awaited<ReturnType<ReturnType<typeof useClerk>["client"]["signIn"]["create"]>>;

/** Clerk's own sentence for a failed request; never the typed address or code. */
function clerkErrorMessage(error: unknown): string {
  const first = (error as { errors?: ReadonlyArray<{ longMessage?: string; message?: string }> })
    ?.errors?.[0];
  return first?.longMessage ?? first?.message ?? "Something went wrong. Try again.";
}

const OTHER_STEP: Partial<Record<SignInAttempt["status"] & string, string>> = {
  needs_second_factor: "This account uses two-step verification, which this form can't do.",
  needs_protect_check:
    "Clerk wants an extra security check for this sign-in, which this form can't do.",
  needs_client_trust: "Clerk wants to confirm this device first, which this form can't do.",
  needs_new_password: "This account has to set a new password, which this form can't do.",
};

function otherStepMessage(status: SignInAttempt["status"]): string {
  return (
    (status && OTHER_STEP[status]) ??
    "This account needs another sign-in step that this form can't do."
  );
}

// A complete sign-in can still leave its session pending, on a task (choosing an organization, setting up two-step
// verification, resetting a password). Clerk counts a pending session as signed out, so the form must not close on it.
const PENDING_SESSION_MESSAGE =
  "Clerk needs you to finish setting up this account (for example choosing an organization or setting up two-step verification), which this form can't do.";

type Step =
  | { readonly kind: "email" }
  | { readonly kind: "code"; readonly emailAddressId: string }
  | { readonly kind: "other"; readonly message: string };

function DeskEmailCodeSignInForm({
  title,
  onBusyChange,
  onSignedIn,
  onUseFullSignIn,
}: {
  readonly title: string;
  readonly onBusyChange: (busy: boolean) => void;
  readonly onSignedIn: () => void;
  readonly onUseFullSignIn: (emailAddress: string) => void;
}) {
  const clerk = useClerk();
  const attempt = useRef<SignInAttempt | null>(null);
  const [step, setStep] = useState<Step>({ kind: "email" });
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const errorId = useId();

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    onBusyChange(true);
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (cause) {
      setError(clerkErrorMessage(cause));
    } finally {
      setBusy(false);
      onBusyChange(false);
    }
  };

  // The sign-in can't go on in this form: drop it, so nothing can attempt its finished first factor again.
  const toOtherStep = (message: string) => {
    attempt.current = null;
    setCode("");
    setStep({ kind: "other", message });
  };

  const sendCode = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const signIn = await clerk.client.signIn.create({ identifier: email.trim() });
      if (signIn.status !== "needs_first_factor") {
        toOtherStep(otherStepMessage(signIn.status));
        return;
      }
      const factor = signIn.supportedFirstFactors?.find(
        (f) => f.strategy === DESK_SIGN_IN_STRATEGY,
      );
      if (!factor || !("emailAddressId" in factor)) {
        setError("This account can't get a sign-in code by email.");
        return;
      }
      attempt.current = await signIn.prepareFirstFactor({
        strategy: DESK_SIGN_IN_STRATEGY,
        emailAddressId: factor.emailAddressId,
      });
      setCode("");
      setStep({ kind: "code", emailAddressId: factor.emailAddressId });
    });
  };

  const sendNewCode = (emailAddressId: string) => {
    void run(async () => {
      const signIn = attempt.current;
      if (!signIn) return;
      attempt.current = await signIn.prepareFirstFactor({
        strategy: DESK_SIGN_IN_STRATEGY,
        emailAddressId,
      });
      setCode("");
      setNotice("We sent a new code.");
    });
  };

  const verifyCode = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const signIn = attempt.current;
      if (!signIn) return;
      const result = await signIn.attemptFirstFactor({
        strategy: DESK_SIGN_IN_STRATEGY,
        code,
      });
      if (result.status !== "complete") {
        toOtherStep(otherStepMessage(result.status));
        return;
      }
      const sessionId = result.createdSessionId;
      if (clerk.client.sessions.find((s) => s.id === sessionId)?.status === "pending") {
        toOtherStep(PENDING_SESSION_MESSAGE);
        return;
      }
      await clerk.setActive({ session: sessionId });
      if (clerk.session?.id !== sessionId || clerk.session.status !== "active") {
        toOtherStep(PENDING_SESSION_MESSAGE);
        return;
      }
      onSignedIn();
    });
  };

  const backToEmail = () => {
    attempt.current = null;
    setError(null);
    setNotice(null);
    setStep({ kind: "email" });
  };

  if (step.kind === "other") {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{step.message} Use the full sign-in to finish.</DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <div className="flex flex-col gap-3">
            <Button type="button" onClick={() => onUseFullSignIn(email.trim())}>
              Use the full sign-in
            </Button>
            <Button type="button" variant="ghost" onClick={backToEmail}>
              Use a different email
            </Button>
          </div>
        </DialogPanel>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {step.kind === "email"
            ? "Enter your email address. We'll email you a 6-digit code."
            : `Enter the 6-digit code we sent to ${email.trim()}.`}
        </DialogDescription>
      </DialogHeader>
      <DialogPanel>
        <form
          className="flex flex-col gap-3"
          onSubmit={step.kind === "email" ? sendCode : verifyCode}
          noValidate
        >
          {step.kind === "email" ? (
            <Input
              nativeInput
              aria-label="Email address"
              autoComplete="email"
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? true : undefined}
              autoFocus
              name="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
          ) : (
            <Input
              nativeInput
              aria-label="Code"
              aria-describedby={error ? errorId : undefined}
              aria-invalid={error ? true : undefined}
              autoComplete="one-time-code"
              autoFocus
              inputMode="numeric"
              name="code"
              value={code}
              // A pasted "123 456" keeps its six digits: strip everything else, then cap at six.
              onChange={(event) =>
                setCode(event.currentTarget.value.replace(/\D/gu, "").slice(0, 6))
              }
            />
          )}
          {error ? (
            <p id={errorId} role="alert" className="text-destructive-foreground text-sm">
              {error}
            </p>
          ) : null}
          {notice ? (
            <p role="status" className="text-muted-foreground text-sm">
              {notice}
            </p>
          ) : null}
          <Button
            type="submit"
            disabled={busy || (step.kind === "email" ? !email.trim() : code.length !== 6)}
          >
            {step.kind === "email" ? "Email me a code" : "Sign in"}
          </Button>
          {step.kind === "code" ? (
            <>
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => sendNewCode(step.emailAddressId)}
              >
                Send a new code
              </Button>
              <Button type="button" variant="ghost" disabled={busy} onClick={backToEmail}>
                Use a different email
              </Button>
            </>
          ) : null}
        </form>
      </DialogPanel>
    </>
  );
}

export function DeskEmailCodeSignInDialog({
  title = "Sign in to Cleo Connect",
  open,
  onOpenChange,
  onUseFullSignIn,
}: {
  /** The window's one Clerk session serves Cleo Connect and the World alike (WorldClerkSession.tsx). */
  readonly title?: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onUseFullSignIn: (emailAddress: string) => void;
}) {
  // While a request is in flight, Escape, an outside click and the close button do nothing, so a late setActive
  // never runs behind a form the person already dismissed.
  const busy = useRef(false);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy.current) return;
        onOpenChange(next);
      }}
    >
      <DialogPopup className="max-w-sm">
        {open ? (
          <DeskEmailCodeSignInForm
            title={title}
            onBusyChange={(value) => {
              busy.current = value;
            }}
            onSignedIn={() => onOpenChange(false)}
            onUseFullSignIn={(emailAddress) => {
              onOpenChange(false);
              onUseFullSignIn(emailAddress);
            }}
          />
        ) : null}
      </DialogPopup>
    </Dialog>
  );
}
